/**
 * Resolve a user-facing, localized message from any error thrown by the API
 * layer (see `fetchApi.ts`). Centralizes the mapping so backend `code`s /
 * HTTP status / validation payloads become friendly text in the user's
 * current language (pt/en) — instead of raw strings like
 * "Request failed with status 422".
 *
 * Resolution order:
 *   1. errors.codes.<E_CODE>   — backend domain error code (most specific)
 *   2. errors.validation       — VineJS 422 (field validation, no top-level code)
 *   3. errors.status.<http>    — HTTP status fallback
 *   4. errors.network          — fetch/connection failure (no status)
 *   5. backend message         — only if it isn't the generic fetch fallback
 *   6. errors.generic          — last resort
 */
import i18nInstance from "@/app/i18n/config";
import type { ApiError } from "@/shared/utils/fetchApi";

type Translator = (key: string, options?: Record<string, unknown>) => string;

interface NormalizedError {
  status?: number;
  code?: string;
  validationMessages: string[];
  rawMessage?: string;
}

// fetchApi's own fallback message when the backend body has no `message`.
const GENERIC_FETCH_MESSAGE_RE = /^request failed with status \d+$/i;
const NETWORK_ERROR_RE = /failed to fetch|networkerror|load failed|network request failed/i;

function normalize(error: unknown): NormalizedError {
  const e = (error ?? {}) as Partial<ApiError> & {
    data?: { code?: string; message?: string; errors?: Array<{ message?: string }> };
    response?: { status?: number; data?: { code?: string; errors?: Array<{ message?: string }> } };
  };

  const data = e.data ?? e.response?.data;
  const status = e.status ?? e.response?.status;
  const code = e.code ?? data?.code;

  const rawErrors = Array.isArray(data?.errors) ? data!.errors : [];
  const validationMessages = rawErrors
    .map((x) => x?.message)
    .filter((m): m is string => typeof m === "string" && m.length > 0);

  const rawMessage = typeof e.message === "string" ? e.message : undefined;

  return { status, code, validationMessages, rawMessage };
}

/** The stable error `code` the API sent (e.g. `E_MCP_TOOLS_CHANGED`), when there is one. */
export function apiErrorCode(error: unknown): string | undefined {
  return normalize(error).code;
}

/** Returns the translation if the key resolves, otherwise undefined. */
function tryTranslate(translate: Translator, key: string): string | undefined {
  const value = translate(key);
  // i18next returns the key itself when a translation is missing.
  return value && value !== key ? value : undefined;
}

/**
 * @param error - anything thrown by fetchApi / react-query (ApiError, Error, unknown)
 * @param t - optional translator (e.g. from `useTranslation`). Defaults to the
 *            global i18n instance, so this works outside React (interceptors,
 *            react-query cache handlers).
 */
export function resolveApiErrorMessage(error: unknown, t?: Translator): string {
  const translate: Translator = t ?? ((key, options) => i18nInstance.t(key, options) as string);
  const { status, code, validationMessages, rawMessage } = normalize(error);

  // 1. Specific backend code
  if (code) {
    const byCode = tryTranslate(translate, `errors.codes.${code}`);
    if (byCode) return byCode;
  }

  // 2. Validation (422 with field-level messages, no domain code)
  if (validationMessages.length > 0) {
    return translate("errors.validation");
  }

  // 3. HTTP status fallback
  if (typeof status === "number") {
    const byStatus = tryTranslate(translate, `errors.status.${status}`);
    if (byStatus) return byStatus;
  }

  // 4. Network failure (fetch threw, no HTTP status)
  if (status === undefined && rawMessage && NETWORK_ERROR_RE.test(rawMessage)) {
    return translate("errors.network");
  }

  // 5. Backend message, when it carries real information
  if (
    rawMessage &&
    !GENERIC_FETCH_MESSAGE_RE.test(rawMessage) &&
    rawMessage !== "Falha na autenticação"
  ) {
    return rawMessage;
  }

  // 6. Generic
  return translate("errors.generic");
}

/** Localized title for error toasts (e.g. "Erro" / "Error"). */
export function apiErrorTitle(t?: Translator): string {
  const translate: Translator = t ?? ((key, options) => i18nInstance.t(key, options) as string);
  return translate("errors.title");
}
